import { ModalV2 } from "@/components";
import { Box, Button, Paper, Stack, Typography } from "@mui/material";
import { FC, ReactElement } from "react";
import { getStatusStep, StatusDisplay } from "../../../helpers";
import { roles } from "@/roles";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "@/store";

const FirstSaleProgress: FC<FirstSaleProgressProps> = ({
  open,
  onClose,
  status,
  userClientId,
  manufacturerId,
  manufacturerName,
  openContactVendor,
  openResendContactForm,
}): ReactElement => {
  const currentStep = getStatusStep(status);
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);
  const { selectedTenant } = useAppSelector((state) => state.tenantManagement);
  const isNotEligible = status === "Not eligible for First Sale";

  // Define process steps with the two middle steps removed
  const steps = [
    {
      id: 1,
      title: "Send contact form to vendor",
      description:
        "Initiate the first sale process by contacting the vendor to request needed documentation and questionnaire responses.",
    },
    {
      id: 2,
      title: "Wait for vendor response",
      description:
        "Vendor has been asked to upload documentation and provide questionnaire responses.",
    },
    {
      id: 3,
      title: "Validate and report drafting",
      description:
        "Verify all provided documentation and questionnaire responses are sufficient for analysis. Report documenting first sale validity is being drafted.",
    },
    {
      id: 4,
      title: "Engage customs broker",
      description:
        "Instruction to customs broker to begin claim first sale for this vendor.",
    },
  ];

  const handleClick = (stepId: number) => {
    if (stepId === 1) {
      onClose();
      openContactVendor();
    } else if (stepId === 2) {
      onClose();
      openResendContactForm();
    } else if (stepId === 3) {
      onClose();
      navigate(
        `/dashboard/validate/${manufacturerId}?clientId=${userClientId}${
          user?.userRole === roles.TDADMIN
            ? `&tenantId=${selectedTenant?._id}`
            : ""
        }`
      );
    }
  };

  return (
    <ModalV2
      show={open}
      setShow={onClose}
      buttonText={"Close"}
      hideCancelButton
      hideActionButton
      maxWidth="lg"
      buttonAction={onClose}
      title="First Sale Adjustment"
      subTitle="First Sale Adjustments help reduce duty costs by using the earlier sale price in multi-tiered transactions."
    >
      <Box sx={{ p: 2 }}>
        <Stack spacing={4}>
          <Box
            sx={{
              bgcolor: "background.paper",
              p: 3,
              borderRadius: 1,
              boxShadow: 1,
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="body1" fontWeight={600}>
                Manufacturer:
              </Typography>
              <Typography variant="body1">
                {manufacturerName || manufacturerId ? (
                  <>
                    {manufacturerName || "N/A"} ({manufacturerId || "N/A"})
                  </>
                ) : (
                  "N/A"
                )}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center" mt={1}>
              <Typography variant="body1" fontWeight={600}>
                Current Status:
              </Typography>
              <StatusDisplay
                status={status}
                hideTooltip
                onClick={() => {}}
              />
            </Stack>
          </Box>

          {isNotEligible ? (
            <Paper
              elevation={0}
              sx={{ p: 3, bgcolor: "error.lighter", borderRadius: 1 }}
            >
              <Typography variant="body1" fontWeight={600} color="error.main">
                This manufacturer is not eligible for First Sale treatment
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={1}>
                The vendor documentation or relationship does not meet the
                criteria for First Sale valuation.
              </Typography>
            </Paper>
          ) : (
            <Stack spacing={1}>
              {steps.map((step) => {
                const isActive = step.id === currentStep;
                const isCompleted = step.id < currentStep;

                return (
                  <Box key={step.id} sx={{ position: "relative" }}>
                    {/* Vertical connecting line */}
                    {step.id < steps.length && (
                      <Box
                        sx={{
                          position: "absolute",
                          left: 20,
                          top: 40,
                          bottom: -10,
                          width: 2,
                          bgcolor: isCompleted ? "primary.main" : "grey.300",
                          zIndex: 0,
                        }}
                      />
                    )}

                    <Stack direction="row" spacing={3} alignItems="flex-start">
                      {/* Step circle */}
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "50%",
                          bgcolor: isActive
                            ? "primary.main"
                            : isCompleted
                            ? "primary.light"
                            : "grey.200",
                          color:
                            isActive || isCompleted
                              ? "white"
                              : "text.secondary",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 600,
                          zIndex: 1,
                          position: "relative",
                        }}
                      >
                        {isCompleted ? "✓" : step.id}
                      </Box>

                      {/* Step content */}
                      <Box
                        sx={{
                          flex: 1,
                          p: 2,
                          borderRadius: 1,
                          bgcolor: isActive
                            ? "primary.lighter"
                            : "background.paper",
                          border: isActive ? "1px solid" : "1px solid",
                          borderColor: isActive ? "primary.main" : "grey.300",
                          mb: 2,
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          fontWeight={600}
                          color={
                            isActive
                              ? "primary.main"
                              : isCompleted
                              ? "text.primary"
                              : "text.secondary"
                          }
                        >
                          {step.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          mt={0.5}
                        >
                          {step.description}
                        </Typography>

                        {isActive && (
                          <Box mt={2}>
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={
                                step.id === 1 ? (
                                  <Box component="span">📧</Box>
                                ) : null
                              }
                              onClick={() => handleClick(step.id)}
                            >
                              {step.id === 1
                                ? "Send Contact Form"
                                : step.id === 2
                                ? "Resend Contact Form"
                                : step.id === 3
                                ? "Validate Report"
                                : "Contact Broker"}
                            </Button>
                          </Box>
                        )}
                      </Box>
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          )}
        </Stack>
      </Box>
    </ModalV2>
  );
};

export default FirstSaleProgress;
